import type { MaybeRefOrGetter } from 'vue'
import { computed, onMounted, onUnmounted, toValue } from 'vue'

export const DEFAULT_POLL_INTERVAL_MS = 5000

export function usePolling(
  whenPoll: () => void | Promise<void>,
  options?: {
    intervalMs?: number
    enabled?: MaybeRefOrGetter<boolean>
  },
) {
  const intervalMs = options?.intervalMs ?? DEFAULT_POLL_INTERVAL_MS
  const enabled = computed(() => {
    if (options?.enabled == null) {
      return true
    }
    return toValue(options.enabled)
  })

  let timer: ReturnType<typeof setInterval> | null = null
  let pollInFlight = false

  async function runPollTick() {
    if (!enabled.value) {
      return
    }
    if (typeof document !== 'undefined' && document.hidden) {
      return
    }
    if (pollInFlight) {
      return
    }

    pollInFlight = true
    try {
      await whenPoll()
    }
    catch {
    }
    finally {
      pollInFlight = false
    }
  }

  function stopPolling() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  function startPolling() {
    stopPolling()
    timer = setInterval(() => {
      void runPollTick()
    }, intervalMs)
  }

  function handleVisibilityChange() {
    if (typeof document === 'undefined' || document.hidden) {
      return
    }
    void runPollTick()
  }

  onMounted(() => {
    startPolling()
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }
  })

  onUnmounted(() => {
    stopPolling()
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  })
}
